import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import { Download, Loader2 } from 'lucide-react';
import { getAgencies } from '../../services/api';
import {
  assignUnassignedFax,
  getUnassignedFax,
  getUnassignedFaxDocumentUrl,
  listAgencyClinics,
  listUnassignedFaxes,
  type AgencyClinicOption,
  type UnassignedFax,
  type UnassignedFaxDetail,
} from '../../services/unassignedFaxes';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

/** One screen of the parked queue. The API caps a page at 100. */
const PAGE_SIZE = 50;

interface AgencyOption {
  value: string;
  label: string;
}

/**
 * The faxes no extension mapping could place.
 *
 * A parked fax is hidden from every agency inbox on purpose, so without this
 * panel it is visible to nobody and stays that way. The queue is the work: open
 * one when the sending number is not enough to say whose it is, then hand it to
 * the agency it belongs to.
 *
 * Fixing the mapping is the actual repair and lives in the fax number mapping
 * admin. This clears what already arrived; that stops the queue refilling.
 */
interface UnassignedFaxesPanelProps {
  /**
   * Bumped when something outside this panel places parked faxes.
   *
   * Both fax panels are force-mounted siblings, so this one does not remount
   * when the tab changes and would otherwise keep showing rows that have since
   * found an agency. Assigning one of those fails, so stale here is not merely
   * untidy.
   */
  reloadToken?: number;
}

export const UnassignedFaxesPanel: React.FC<UnassignedFaxesPanelProps> = ({
  reloadToken = 0,
}) => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );

  const [faxes, setFaxes] = useState<UnassignedFax[]>([]);
  const [total, setTotal] = useState(0);
  // Paged, because a backlog is exactly when this screen matters and an agency
  // that has parked more than one page could not reach the older ones at all.
  const [queuePage, setQueuePage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const [opened, setOpened] = useState<UnassignedFaxDetail | null>(null);
  const [assigning, setAssigning] = useState<UnassignedFax | null>(null);
  const [agencies, setAgencies] = useState<AgencyOption[]>([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  const [clinics, setClinics] = useState<AgencyClinicOption[]>([]);
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Guards against a slow response repainting the table after a newer one has
  // already landed, which would put rescued faxes back on screen.
  const lastQueuePage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const loadGeneration = useRef(0);

  const loadQueue = useCallback(async () => {
    const generation = ++loadGeneration.current;
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const page = await listUnassignedFaxes({
        limit: PAGE_SIZE,
        offset: queuePage * PAGE_SIZE,
      });
      if (generation !== loadGeneration.current) return;
      setFaxes(page.items);
      setTotal(page.total);
    } catch (error) {
      if (generation !== loadGeneration.current) return;
      console.error('Failed to load the unassigned fax queue:', error);
      setFaxes([]);
      setLoadFailed(true);
    } finally {
      if (generation === loadGeneration.current) setIsLoading(false);
    }
  }, [queuePage]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue, reloadToken]);

  // Assigning a fax removes it from the queue, so the last page can empty
  // underneath whoever just cleared it.
  useEffect(() => {
    if (queuePage > lastQueuePage) setQueuePage(lastQueuePage);
  }, [lastQueuePage, queuePage]);

  const openFax = async (fax: UnassignedFax) => {
    try {
      setOpened(await getUnassignedFax(fax.id));
    } catch (error) {
      console.error('Failed to open the fax:', error);
      showError('Could not open fax', 'Please try again.');
    }
  };

  const openDocument = async (faxId: string, documentId: string) => {
    // Opened rather than fetched: the bucket serves it as an attachment from
    // its own origin, which keeps an HTML cover page from an unvetted sender
    // out of this page.
    //
    // Opened before the await, while the click is still the user's gesture.
    // After one it is gone and the blocker stops the tab, which is the whole
    // failure this shape avoids.
    const tab = window.open('', '_blank');
    if (!tab) {
      showError(
        'Could not open document',
        'Your browser blocked the tab. Allow pop-ups for this site and try again.'
      );
      return;
    }
    try {
      tab.location.href = await getUnassignedFaxDocumentUrl(faxId, documentId);
    } catch (error) {
      tab.close();
      console.error('Failed to get the document URL:', error);
      showError('Could not open document', 'Please try again.');
    }
  };

  const startAssigning = async (fax: UnassignedFax) => {
    setAssigning(fax);
    setSelectedAgencyId('');
    setClinics([]);
    setSelectedClinicId('');
    if (agencies.length > 0) return;
    try {
      const response = await getAgencies({
        query: { limit: 1000 },
        throwOnError: true,
      });
      setAgencies(
        (response.data ?? []).map(agency => ({
          value: String(agency.id),
          label: agency.system_name ?? String(agency.id),
        }))
      );
    } catch (error) {
      console.error('Failed to load agencies:', error);
      showError('Could not load agencies', 'Please try again.');
    }
  };

  // Clinics belong to the chosen agency, so they cannot be loaded until there
  // is one, and they have to be dropped again when it changes.
  const chooseAgency = async (agencyId: string) => {
    setSelectedAgencyId(agencyId);
    setClinics([]);
    setSelectedClinicId('');
    if (!agencyId) return;
    try {
      setClinics(await listAgencyClinics(agencyId));
    } catch (error) {
      console.error('Failed to load clinics:', error);
      showError('Could not load clinics', 'You can still assign the agency.');
    }
  };

  const confirmAssign = async () => {
    if (!assigning || !selectedAgencyId) return;
    setIsSaving(true);
    try {
      await assignUnassignedFax(assigning.id, {
        agency_id: selectedAgencyId,
        // Optional on purpose: left unset the fax stays visible to the whole
        // agency, which is the right default when the clinic is not known.
        agency_clinic_id: selectedClinicId || null,
      });
      showSuccess('Fax assigned', 'It is now in that agency inbox.');
      setAssigning(null);
      await loadQueue();
    } catch (error) {
      console.error('Failed to assign the fax:', error);
      // An agency without eFax is refused for good, so retrying cannot help.
      showError(
        'Could not assign fax',
        getBusinessErrorMessage(error) ?? 'Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Unassigned Faxes</CardTitle>
        <CardDescription>
          Faxes that arrived on an extension with no active mapping. They are
          hidden from every agency until somebody here places them. Add the
          missing mapping in Fax Number Mappings so the next one routes itself.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='flex justify-center py-10'>
            <Loader2 className='h-6 w-6 animate-spin' />
          </div>
        ) : loadFailed ? (
          <div className='space-y-3 py-10 text-center'>
            <p className='text-destructive'>Could not load the queue.</p>
            <Button
              variant='outline'
              size='sm'
              onClick={() => void loadQueue()}
            >
              Try again
            </Button>
          </div>
        ) : faxes.length === 0 ? (
          <p className='py-10 text-center text-muted-foreground'>
            Nothing waiting. Every fax that arrived found an agency.
          </p>
        ) : (
          <>
            <p className='mb-3 text-sm text-muted-foreground'>
              {total} waiting, oldest first.
            </p>
            <div className='overflow-x-auto'>
              <Table className='min-w-[720px]'>
                <TableHeader>
                  <TableRow>
                    <TableHead>Received</TableHead>
                    <TableHead>Extension</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Pages</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {faxes.map(fax => (
                    <TableRow key={fax.id}>
                      <TableCell>
                        {formatDateForDisplay(fax.received_at)}
                      </TableCell>
                      <TableCell>
                        {/* The ownerId is what the mapping lookup keys on, so
                            it is the value somebody copies into the mapping. */}
                        {fax.ringcentral_owner_id ?? 'Unknown'}
                        {fax.ringcentral_extension_id &&
                          fax.ringcentral_extension_id !==
                            fax.ringcentral_owner_id && (
                            <span
                              className='ml-2 whitespace-nowrap text-xs text-destructive'
                              title='The webhook names this extension as the one the fax arrived on, but the subscription is owned by the extension on the left. Routing will not trust either while they disagree, so the fax waits here. Fix the subscription rather than adding a mapping.'
                            >
                              {/* "path" was the mechanism, not the meaning.
                                  What an operator needs to know is which
                                  extension actually took the call. */}
                              arrived on {fax.ringcentral_extension_id}
                            </span>
                          )}
                      </TableCell>
                      <TableCell>{fax.fax_number_from ?? 'Unknown'}</TableCell>
                      <TableCell className='max-w-[22rem] truncate'>
                        {/* Usually the patient or the sending practice, which
                            is often enough to route without opening the fax. */}
                        {fax.subject || '\u2014'}
                      </TableCell>
                      <TableCell>{fax.page_count ?? '?'}</TableCell>
                      <TableCell className='text-right'>
                        {/* One row: the column is narrow enough that the
                            default inline spacing wrapped them into a stack. */}
                        <div className='flex items-center justify-end gap-2 whitespace-nowrap'>
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => void openFax(fax)}
                          >
                            Open
                          </Button>
                          <Button
                            size='sm'
                            onClick={() => void startAssigning(fax)}
                          >
                            Assign
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {total > PAGE_SIZE && (
              <div className='mt-3 flex items-center justify-end gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  disabled={queuePage === 0}
                  onClick={() =>
                    setQueuePage(current => Math.max(0, current - 1))
                  }
                >
                  Previous
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  disabled={queuePage >= lastQueuePage}
                  onClick={() => setQueuePage(current => current + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      <Dialog
        open={opened !== null}
        onOpenChange={open => !open && setOpened(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fax with no agency</DialogTitle>
            <DialogDescription>
              Open a document to work out whose this is. Each download is
              recorded.
            </DialogDescription>
          </DialogHeader>
          {opened && (
            <div className='space-y-3'>
              <dl className='grid grid-cols-2 gap-1 text-sm'>
                <dt className='text-muted-foreground'>Received</dt>
                <dd>{formatDateForDisplay(opened.received_at)}</dd>
                <dt className='text-muted-foreground'>Extension</dt>
                <dd>{opened.ringcentral_owner_id ?? 'Unknown'}</dd>
                <dt className='text-muted-foreground'>From</dt>
                <dd>{opened.fax_number_from ?? 'Unknown'}</dd>
                <dt className='text-muted-foreground'>Subject</dt>
                <dd>{opened.subject || '\u2014'}</dd>
                <dt className='text-muted-foreground'>Pages</dt>
                <dd>{opened.page_count ?? '?'}</dd>
              </dl>
              {opened.documents.length === 0 ? (
                <p className='text-sm text-muted-foreground'>
                  No documents arrived with this fax.
                </p>
              ) : (
                opened.documents.map((document, index) => (
                  <div
                    key={document.id}
                    className='flex items-center justify-between rounded border p-2'
                  >
                    <span className='text-sm'>Document {index + 1}</span>
                    <Button
                      variant='outline'
                      size='sm'
                      disabled={document.status !== 'ready'}
                      onClick={() => void openDocument(opened.id, document.id)}
                    >
                      <Download className='mr-2 h-4 w-4' />
                      {document.status === 'ready' ? 'Open' : document.status}
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={assigning !== null}
        onOpenChange={open => !open && setAssigning(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign this fax</DialogTitle>
            <DialogDescription>
              The fax moves into that agency inbox and leaves this queue. It
              cannot be moved again afterwards, so check before you confirm.
            </DialogDescription>
          </DialogHeader>
          <VirtualizedFormSelect
            key={assigning ? `assign-${assigning.id}` : 'closed'}
            label='Agency'
            placeholder='Select an agency'
            options={agencies}
            sortOptions
            defaultValue={selectedAgencyId}
            onChange={event => void chooseAgency(event.target.value)}
            isClearable={false}
          />
          {clinics.length > 0 && (
            <VirtualizedFormSelect
              key={`clinic-${selectedAgencyId}`}
              label='Clinic (optional)'
              placeholder='Whole agency'
              options={clinics.map(clinic => ({
                value: clinic.id,
                label: clinic.name,
              }))}
              sortOptions
              defaultValue={selectedClinicId}
              onChange={event => setSelectedClinicId(event.target.value)}
              isClearable
            />
          )}
          <DialogFooter>
            <Button variant='outline' onClick={() => setAssigning(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => void confirmAssign()}
              disabled={!selectedAgencyId || isSaving}
            >
              {isSaving ? 'Assigning...' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default UnassignedFaxesPanel;
